/* ============================================================
   Cart ETA Refresh — Baby Gold / Broadcast theme
   ------------------------------------------------------------
   Recomputes the "Estimated Delivery" line item properties when
   the cart drawer opens, so the cart shows CURRENT lead times
   instead of whatever was stamped at add-to-cart.

   Strategy:
   - While the drawer is OPEN: patch the DOM text directly only.
     Never call /cart/change.js while Broadcast is active —
     it triggers getCart() → full re-render → qty corruption.
   - When the drawer CLOSES: write correct properties to the
     cart API. Must include quantity in the payload to prevent
     Frenzy from intercepting and making additional cart calls.
   - On drawer OPEN: DOM-only patch so message is visually correct.
   - On CHECKOUT: programmatically close the drawer first, which
     triggers the safe API write, then proceed to checkout.

   Ported logic (matches the theme's writer exactly):
   - Message rules come from the #lead-time-message JSON config
     [{ message_tag, message_content, above_or_below_zero }, ...]
   - First product tag with a matching config entry wins, where
     above_zero requires live inventory > 0 and below_zero
     requires live inventory <= 0.
   ============================================================ */

(function () {
  'use strict';

  var CONFIG = {
    PRIMARY_KEY: 'Estimated Delivery',
    MIRROR_KEYS: ['_check_s_m_trigger'],
    QTY_KEY: '_default_invent_qty',

    DRAWER_SELECTOR: '#cart-drawer',

    SKIP_TITLE_PATTERNS: [/package protection/i, /free returns/i],

    COOLDOWN_MS: 15000,

    INTERCEPT_CUSTOM_CHECKOUT_BUTTONS: true,
    CUSTOM_CHECKOUT_EXCLUDE: [/checkout\s*\+/i],

    DEBUG: false
  };

  function dbg() {
    if (CONFIG.DEBUG) console.log.apply(console, ['[cart-eta-refresh]'].concat([].slice.call(arguments)));
  }

  var lastRun = 0;
  var running = false;
  var productCache = {};
  var inventoryCache = {};
  var leadTimeMapPromise = null;

  /* ---------------- helpers ---------------- */

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('Fetch failed: ' + url + ' (' + r.status + ')');
      return r.json();
    });
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = getJSON('/products/' + handle + '.js');
    }
    return productCache[handle];
  }

  function stripHtml(s) {
    return String(s || '').replace(/<[^>]+>/g, '');
  }

  function isDrawerOpen() {
    var drawer = document.querySelector(CONFIG.DRAWER_SELECTOR);
    return drawer ? drawer.classList.contains('is-open') : false;
  }

  // Close the drawer programmatically and return a promise that resolves
  // once the drawer-close animation has finished and runApiWrite completes.
  function closeDrawerAndWrite() {
    return new Promise(function (resolve) {
      if (!isDrawerOpen()) {
        resolve();
        return;
      }
      dbg('closing drawer for checkout — runApiWrite will fire on close');
      var drawer = document.querySelector(CONFIG.DRAWER_SELECTOR);
      var closeBtn = drawer && drawer.querySelector('[data-cart-drawer-close]');

      // Listen for runApiWrite to finish before resolving.
      // We do this by watching for the drawer to lose is-open, then
      // waiting for runApiWrite (triggered by watchDrawer) to complete.
      var observer = new MutationObserver(function () {
        if (!drawer.classList.contains('is-open')) {
          observer.disconnect();
          // Give runApiWrite time to complete (it fires 100ms after close).
          setTimeout(resolve, 2500);
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

      if (closeBtn) {
        closeBtn.click();
      } else {
        // Fallback: remove is-open directly.
        drawer.classList.remove('is-open');
      }

      // Safety timeout in case observer never fires.
      setTimeout(function () { observer.disconnect(); resolve(); }, 4000);
    });
  }

  /* ----- lead-time config ----- */
  function getLeadTimeMap(fallbackProductUrl) {
    if (leadTimeMapPromise) return leadTimeMapPromise;

    var el = document.getElementById('lead-time-message');
    if (el) {
      try {
        var arr = JSON.parse(el.textContent || el.innerHTML);
        leadTimeMapPromise = Promise.resolve(buildMap(arr));
        return leadTimeMapPromise;
      } catch (e) {}
    }

    if (!fallbackProductUrl) return Promise.resolve(null);

    leadTimeMapPromise = fetch(fallbackProductUrl)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.getElementById('lead-time-message');
        if (!node) return null;
        return buildMap(JSON.parse(node.textContent || node.innerHTML));
      })
      .catch(function () { return null; });

    return leadTimeMapPromise;

    function buildMap(messages) {
      var map = new Map();
      (messages || []).forEach(function (m) { map.set(m.message_tag, m); });
      return map.size ? map : null;
    }
  }

  /* ----- live inventory ----- */
  function getLiveInventory(handle, variantId) {
    var key = handle + ':' + variantId;
    if (!inventoryCache[key]) {
      var root = (window.theme && theme.routes && theme.routes.root) || '/';
      var base = root + 'products/' + handle + '?variant=' + variantId + '&section_id=';
      inventoryCache[key] = fetch(base + 'api-eta-inventory')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-eta-inventory]');
          if (node) {
            if (node.getAttribute('data-tracked') !== 'true') return 0;
            return Number(node.getAttribute('data-qty'));
          }
          return fetch(base + 'api-live-cart-info')
            .then(function (r2) { return r2.text(); })
            .then(function (html2) {
              var doc2 = new DOMParser().parseFromString(html2, 'text/html');
              var n2 = doc2.querySelector('[data-max-inventory]');
              var raw = n2 ? n2.innerHTML.trim() : '';
              return raw === '' ? 0 : Number(raw);
            });
        })
        .catch(function () { return null; });
    }
    return inventoryCache[key];
  }

  /* ----- message rules ----- */
  function computeMessage(ltMap, tags, count) {
    if (!ltMap || count === null || isNaN(count)) return null;
    for (var i = 0; i < tags.length; i++) {
      var matched = ltMap.get(tags[i]);
      if (!matched) continue;
      var messageText = stripHtml(matched.message_content);
      var above = matched.above_or_below_zero === 'above_zero';
      if ((count > 0 && above) || (count <= 0 && !above)) {
        return messageText;
      }
    }
    return null;
  }

  /* ----- write properties to cart API -----
     Must include quantity in payload — without it Frenzy intercepts
     the /cart/change.js call and makes additional cart mutations
     that corrupt the line item quantity. */
  function changeLine(key, quantity, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, properties: properties })
    }).then(function (r) {
      if (r.status === 400 || r.status === 404) {
        dbg('line ' + key + ' is stale (' + r.status + '), skipping');
        return null;
      }
      if (!r.ok) throw new Error('cart/change failed: ' + r.status);
      return r.json();
    });
  }

  /* ----- patch visible text directly in the DOM ----- */
  function updateDom(updates) {
    updates.forEach(function (u) {
      var item = document.querySelector('[data-item="' + u.key + '"]') ||
                 document.querySelector('[data-item-id="' + u.productId + '"]');
      if (!item) { dbg('DOM: no element for key', u.key, 'productId', u.productId); return; }
      var span = item.querySelector('p.cart__item__property.ships-by-message span');
      if (span) {
        dbg('DOM: patching', u.key, '->', u.properties[CONFIG.PRIMARY_KEY]);
        span.textContent = u.properties[CONFIG.PRIMARY_KEY];
      } else {
        dbg('DOM: no span for', u.key);
      }
    });
  }

  /* ---------------- compute updates ---------------- */

  function computeUpdates(cart, ltMap, domOnly) {
    var items = (cart.items || []).filter(function (item) {
      return !CONFIG.SKIP_TITLE_PATTERNS.some(function (re) {
        return re.test(item.product_title || item.title || '');
      });
    });

    if (!items.length) return Promise.resolve([]);

    var variantAllocated = {};
    var updates = [];

    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return Promise.all([
          getProduct(item.handle),
          getLiveInventory(item.handle, item.variant_id)
        ]).then(function (results) {
          var product = results[0];
          var liveStock = results[1];
          var variantId = item.variant_id;
          var allocatedSoFar = variantAllocated[variantId] || 0;

          var remainingStock = Math.max(0, liveStock - allocatedSoFar);
          var count = remainingStock >= item.quantity ? remainingStock : 0;
          variantAllocated[variantId] = allocatedSoFar + item.quantity;

          var fresh = computeMessage(ltMap, product.tags || [], count);
          if (fresh === null) {
            dbg('item "' + item.product_title + '": no rule matched (liveStock=' + liveStock + ', allocated=' + allocatedSoFar + ', count=' + count + ')');
            return;
          }

          dbg('item "' + item.product_title + '": liveStock=' + liveStock + ', allocated=' + allocatedSoFar + ', qty=' + item.quantity + ', count=' + count + ', computed=' + JSON.stringify(fresh) + ', stored=' + JSON.stringify((item.properties || {})[CONFIG.PRIMARY_KEY]));

          var props = item.properties || {};
          var qtyValue = String(count);
          var unchanged = props[CONFIG.PRIMARY_KEY] === fresh &&
            CONFIG.MIRROR_KEYS.every(function (k) { return props[k] === fresh; }) &&
            (props[CONFIG.QTY_KEY] === undefined || props[CONFIG.QTY_KEY] === qtyValue);

          if (unchanged && !domOnly) return;

          var merged = Object.assign({}, props);
          merged[CONFIG.PRIMARY_KEY] = fresh;
          CONFIG.MIRROR_KEYS.forEach(function (k) { merged[k] = fresh; });
          if (props[CONFIG.QTY_KEY] !== undefined) merged[CONFIG.QTY_KEY] = qtyValue;

          updates.push({ key: item.key, quantity: item.quantity, productId: item.product_id, properties: merged });
        }).catch(function (err) {
          dbg('item "' + item.product_title + '" FAILED:', err && err.message || err);
        });
      });
    }, Promise.resolve()).then(function () {
      return updates;
    });
  }

  /* ---------------- run modes ---------------- */

  function runDomOnly() {
    dbg('runDomOnly started');
    inventoryCache = {};
    return getJSON('/cart.js').then(function (cart) {
      if (!cart.items || !cart.items.length) return;
      return getLeadTimeMap(cart.items[0].url).then(function (ltMap) {
        if (!ltMap) return;
        return computeUpdates(cart, ltMap, true).then(function (updates) {
          dbg('DOM updates:', updates.length);
          if (updates.length) updateDom(updates);
        });
      });
    }).catch(function (err) { console.warn('[cart-eta-refresh] runDomOnly:', err); });
  }

  function runApiWrite() {
    var now = Date.now();
    if (running || now - lastRun < CONFIG.COOLDOWN_MS) {
      dbg('runApiWrite skipped: running=' + running + ' cooldown=' + Math.max(0, CONFIG.COOLDOWN_MS - (now - lastRun)) + 'ms');
      return Promise.resolve();
    }
    dbg('runApiWrite started');
    running = true;
    inventoryCache = {};

    return getJSON('/cart.js').then(function (cart) {
      if (!cart.items || !cart.items.length) return;
      return getLeadTimeMap(cart.items[0].url).then(function (ltMap) {
        if (!ltMap) return;
        return computeUpdates(cart, ltMap, false).then(function (updates) {
          dbg('API updates to apply:', updates.length);
          return updates.reduce(function (chain, u) {
            return chain.then(function () {
              return changeLine(u.key, u.quantity, u.properties).then(function () {
                dbg('wrote line', u.key);
              });
            });
          }, Promise.resolve());
        });
      });
    })
    .catch(function (err) { console.warn('[cart-eta-refresh] runApiWrite:', err); })
    .finally(function () {
      lastRun = Date.now();
      running = false;
    });
  }

  /* ---------------- triggers ---------------- */

  function watchDrawer() {
    var drawer = document.querySelector(CONFIG.DRAWER_SELECTOR);
    if (!drawer) return false;
    var wasOpen = drawer.classList.contains('is-open');

    new MutationObserver(function () {
      var isOpen = drawer.classList.contains('is-open');

      if (isOpen && !wasOpen) {
        dbg('drawer opened — runDomOnly');
        setTimeout(runDomOnly, 300);
      }

      if (!isOpen && wasOpen) {
        dbg('drawer closed — runApiWrite');
        lastRun = 0;
        setTimeout(runApiWrite, 100);
      }

      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    return true;
  }
  if (!watchDrawer()) {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  }
  // Redo's Checkout+ lives inside a shadow DOM — intercept clicks on the
  // redo-shopify-toggle element and close drawer + write before proceeding.
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('redo-shopify-toggle');
    if (!toggle) return;
    if (!isDrawerOpen()) return;
    dbg('redo-shopify-toggle clicked — closing drawer first');
    e.preventDefault();
    e.stopImmediatePropagation();
    closeDrawerAndWrite().then(function () {
      toggle.click();
    });
  }, true);

  // After Broadcast re-renders (qty change), patch DOM immediately.
  document.addEventListener('theme:cart:change', function () {
    if (isDrawerOpen()) setTimeout(runDomOnly, 100);
  });

  // Manual hook.
  window.cartEtaRefresh = function () {
    CONFIG.DEBUG = true;
    lastRun = 0;
    runApiWrite();
  };

  // Cart page.
  if (location.pathname === '/cart') {
    document.addEventListener('DOMContentLoaded', function () {
      lastRun = 0;
      runApiWrite();
    });
  }

  // Tab refocus.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && !isDrawerOpen()) {
      runApiWrite();
    }
  });

  // Checkout submit — close drawer first (triggers safe API write), then proceed.
  var checkoutPassthrough = false;
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('[data-cart-form], form[action^="/cart"]')) return;
    var submitter = e.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (checkoutPassthrough) { checkoutPassthrough = false; return; }

    e.preventDefault();
    dbg('checkout intercepted — closing drawer then proceeding');

    var done = false;
    function proceed() {
      if (done) return;
      done = true;
      checkoutPassthrough = true;
      form.requestSubmit(submitter);
    }

    // Hard 5s failsafe so checkout always continues.
    setTimeout(proceed, 5000);

    closeDrawerAndWrite().then(proceed);
  }, true);

  // Custom checkout buttons (Checkout+, Checkout without protection, etc.)
  document.addEventListener('click', function (e) {
    if (!CONFIG.INTERCEPT_CUSTOM_CHECKOUT_BUTTONS) return;
    var btn = e.target.closest('button, a, [role="button"]');
    if (!btn) return;
    if (btn.dataset.etaPassthrough) { delete btn.dataset.etaPassthrough; return; }
    if (btn.name === 'checkout') return;

    var inCartContext = btn.closest(CONFIG.DRAWER_SELECTOR) || btn.closest('[data-cart-form]') || location.pathname === '/cart';
    if (!inCartContext) return;
    var label = (btn.textContent || '').trim();
    if (!/checkout/i.test(label)) return;
    if (CONFIG.CUSTOM_CHECKOUT_EXCLUDE.some(function (re) { return re.test(label); })) {
      dbg('checkout button excluded:', label);
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    dbg('custom checkout button intercepted — closing drawer then proceeding:', label);

    var done = false;
    function proceed() {
      if (done) return;
      done = true;
      btn.dataset.etaPassthrough = '1';
      btn.click();
    }

    setTimeout(proceed, 5000);

    closeDrawerAndWrite().then(proceed);
  }, true);
})();